/**
 * The people Waterlily holds more than once for this advisor, and how they resolve them.
 *
 * Duplicates arise from connecting a second CRM, or from one CRM holding someone twice. Only the
 * advisor can say whether two records are the same person, because the difference between them is
 * often something only they can read — a maiden name, a work address, a household.
 *
 * Resolving never deletes: it records which record to treat as the person. That keeps the decision
 * reversible, and keeps the promise that cached data is retained rather than quietly removed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';

export function useCrmDuplicates() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.prospecting.duplicates(),
    queryFn: () => prospectingApi.duplicates.list(),
  });

  const mutation = useMutation({
    mutationFn: (decision: {
      matchKey: string;
      decision: Prospecting.DuplicateStatus;
      keptProspectId?: string | null;
    }) => prospectingApi.duplicates.resolve(decision),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.prospecting.duplicates() }),
  });

  const groups = query.data?.groups ?? [];

  return {
    groups,
    /** Groups the advisor has not decided on, plus those whose decision no longer fits. */
    needsAttention: groups.filter(group => group.status === 'unresolved' || group.needsReview),
    unresolved: query.data?.unresolved ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    keepOne: (matchKey: string, keptProspectId: string) =>
      mutation.mutate({ matchKey, decision: 'keepOne', keptProspectId }),
    keepBoth: (matchKey: string) => mutation.mutate({ matchKey, decision: 'keepBoth' }),
    isResolving: mutation.isPending,
    resolveError: mutation.error,
  };
}
